import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Sparkles, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BOOKING_STATES = ["angefragt", "bestätigt", "erledigt", "storniert"];
const SERVICE_STATES = ["offen", "in Arbeit", "erledigt", "abgelehnt"];

function StatusButtons({ states, current, onPick, testid }) {
  return (
    <div className="flex flex-wrap gap-1.5 mt-3">
      {states.map(s => (
        <button key={s} onClick={() => onPick(s)} data-testid={`${testid}-${s}`}
          className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${current === s ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-600 border-slate-200 hover:border-indigo-300"}`}>
          {s}
        </button>
      ))}
    </div>
  );
}

export function AdminDashboardRequests({ adminToken }) {
  const [data, setData] = useState(null);
  const headers = { Authorization: `Bearer ${adminToken}` };

  const load = useCallback(() => {
    axios.get(`${API}/admin/dashboard-requests`, { headers: { Authorization: `Bearer ${adminToken}` } })
      .then(r => setData(r.data)).catch(() => toast.error("Anfragen konnten nicht geladen werden"));
  }, [adminToken]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (kind, id, status) => {
    try {
      await axios.patch(`${API}/admin/dashboard-requests/${kind}/${id}`, { status }, { headers });
      toast.success(kind === "booking" && status === "bestätigt" ? "Termin bestätigt – Kunde per E-Mail informiert" : "Status aktualisiert");
      load();
    } catch { toast.error("Status konnte nicht geändert werden"); }
  };

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-indigo-600" /></div>;

  return (
    <div className="grid gap-8 lg:grid-cols-2" data-testid="admin-dashboard-requests">
      <section>
        <h2 className="font-bold text-slate-900 flex items-center gap-2 mb-3"><Phone className="w-4 h-4 text-indigo-600" />Telefon-Termine ({data.bookings.length})</h2>
        {data.bookings.length === 0 && <p className="text-sm text-slate-400" data-testid="admin-no-bookings">Keine Termine.</p>}
        <div className="space-y-3">
          {data.bookings.map(b => (
            <div key={b.id} className="bg-white rounded-xl border border-slate-100 p-4" data-testid={`admin-booking-${b.id}`}>
              <p className="font-semibold text-slate-900">{b.date.split("-").reverse().join(".")} · {b.time} Uhr</p>
              <p className="text-sm text-slate-600">{b.company_name} · {b.email}</p>
              <p className="text-sm text-slate-600">Tel: {b.phone}{b.topic ? ` · ${b.topic}` : ""}</p>
              <StatusButtons states={BOOKING_STATES} current={b.status} onPick={(s) => setStatus("booking", b.id, s)} testid={`booking-status-${b.id}`} />
            </div>
          ))}
        </div>
      </section>
      <section>
        <h2 className="font-bold text-slate-900 flex items-center gap-2 mb-3"><Sparkles className="w-4 h-4 text-indigo-600" />SEO-Service-Anfragen ({data.service_requests.length})</h2>
        {data.service_requests.length === 0 && <p className="text-sm text-slate-400" data-testid="admin-no-services">Keine Anfragen.</p>}
        <div className="space-y-3">
          {data.service_requests.map(r => (
            <div key={r.id} className="bg-white rounded-xl border border-slate-100 p-4" data-testid={`admin-service-${r.id}`}>
              <p className="font-semibold text-slate-900">{r.title}</p>
              <p className="text-xs text-slate-500">Seite {r.page_id.slice(0, 8)} · {new Date(r.created_at).toLocaleDateString("de-DE")}</p>
              {r.note && <p className="text-sm text-slate-600 mt-1">{r.note}</p>}
              <StatusButtons states={SERVICE_STATES} current={r.status} onPick={(s) => setStatus("service", r.id, s)} testid={`service-status-${r.id}`} />
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
